import { collection, doc, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import type { DocumentData, QueryDocumentSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '@/modules/firebase';
import { normalizeMessage, type MessageActionState, type MessageRecord } from './MessageClient';

const ACTION_WINDOW_MS = 2 * 60 * 60 * 1000;
const MAX_CACHED_FEEDS = 25;
const feedCache = new Map<string, MessageRecord[]>();

export interface MessageFeedActor {
  uid: string;
  isAdmin: boolean;
}

function getFeedCacheKey(recordType: string, recordId: string): string {
  return `${recordType}/${recordId}`;
}

function readCachedFeed(cacheKey: string): MessageRecord[] | null {
  const cached = feedCache.get(cacheKey);
  if (!cached) return null;

  feedCache.delete(cacheKey);
  feedCache.set(cacheKey, cached);
  return cached.map(message => ({ ...message }));
}

function writeCachedFeed(cacheKey: string, messages: MessageRecord[]): void {
  feedCache.delete(cacheKey);
  feedCache.set(cacheKey, messages.map(message => ({ ...message })));

  while (feedCache.size > MAX_CACHED_FEEDS) {
    const oldestKey = feedCache.keys().next().value;
    if (typeof oldestKey !== 'string') break;
    feedCache.delete(oldestKey);
  }
}

/** UI hints only. Mutations still use the callable's authoritative permission checks. */
export function getFeedActionState(
  message: MessageRecord,
  actor: { uid: string; isAdmin: boolean },
  now = Date.now()
): MessageActionState {
  const actionWindowEndsAt = message.timestampMs > 0 ? message.timestampMs + ACTION_WINDOW_MS : null;
  const denied = (reason: MessageActionState['reason'], endsAt = actionWindowEndsAt): MessageActionState => ({
    canEdit: false, canDelete: false, actionWindowEndsAt: endsAt, reason
  });
  if (message.legacy) return denied('legacy-note', null);
  if (message.displayType === 'context') return denied('context', null);
  if (message.deleted) return denied('deleted');
  if (!actor.uid) return denied('not-owner');
  if (actor.isAdmin) return { canEdit: true, canDelete: true, actionWindowEndsAt, reason: 'allowed' };
  if (message.authorId !== actor.uid) return denied('not-owner');
  if (actionWindowEndsAt === null) return denied('timestamp-unavailable');
  if (now > actionWindowEndsAt) return denied('expired');
  return { canEdit: true, canDelete: true, actionWindowEndsAt, reason: 'allowed' };
}

/** Keep the two source windows and legacy mirror checks consistent with listWithLegacy. */
export function subscribeMessageFeed(
  recordType: string,
  recordId: string,
  actor: MessageFeedActor,
  onMessages: (messages: MessageRecord[]) => void,
  onError: (error: Error) => void,
  feedLimit = 200
): Unsubscribe {
  let stopped = false;
  let messageDocs: QueryDocumentSnapshot<DocumentData>[] | null = null;
  let noteDocs: QueryDocumentSnapshot<DocumentData>[] | null = null;
  const subscriptions: Unsubscribe[] = [];
  const mirrors = new Map<string, { exists: boolean | null; unsubscribe: Unsubscribe }>();
  const messagesCollection = collection(db, recordType, recordId, 'messages');
  const cacheKey = getFeedCacheKey(recordType, recordId);

  function stop(): void {
    stopped = true;
    subscriptions.forEach(unsubscribe => unsubscribe());
    mirrors.forEach(mirror => mirror.unsubscribe());
    mirrors.clear();
  }

  function fail(error: Error): void {
    if (stopped) return;
    stop();
    onError(error);
  }

  function publish(): void {
    if (stopped || !messageDocs) return;
    const messageIds = new Set(messageDocs.map(snapshot => snapshot.id));
    const candidates = (noteDocs || []).filter(snapshot => {
      const note = snapshot.data();
      return !messageIds.has(snapshot.id) && note.mirroredFromMessage !== true && note.source !== 'message';
    });
    const candidateIds = new Set(candidates.map(snapshot => snapshot.id));
    mirrors.forEach((mirror, id) => {
      if (!candidateIds.has(id)) {
        mirror.unsubscribe();
        mirrors.delete(id);
      }
    });
    for (const candidate of candidates) {
      if (mirrors.has(candidate.id)) continue;
      // A matching message can be deleted or outside the visible message window.
      // Listen to existence so it cannot reappear as a legacy note after updates.
      const mirror = { exists: null as boolean | null, unsubscribe: (() => {}) as Unsubscribe };
      mirrors.set(candidate.id, mirror);
      mirror.unsubscribe = onSnapshot(
        doc(messagesCollection, candidate.id),
        { includeMetadataChanges: true },
        snapshot => {
          if (stopped || mirrors.get(candidate.id) !== mirror) return;
          // An existing cached document safely proves this note is mirrored. A
          // cached miss can be incomplete, so wait for the server before showing it.
          if (snapshot.metadata.fromCache && !snapshot.exists()) return;
          mirror.exists = snapshot.exists();
          publish();
        },
        () => {
          if (stopped || mirrors.get(candidate.id) !== mirror) return;
          // This auxiliary check only decides whether one old note is safe to
          // display. Suppress that note rather than taking down the live feed.
          mirror.exists = true;
          publish();
        }
      );
    }

    const messages = messageDocs.map(snapshot => normalizeMessage({
      ...snapshot.data(), id: snapshot.id, recordType, recordId,
      authorId: snapshot.data().authorId, source: 'message', legacy: false
    }));
    const notes = candidates.filter(snapshot => mirrors.get(snapshot.id)?.exists === false).map(snapshot => {
      const note = snapshot.data();
      const lines = Array.isArray(note.comment) ? note.comment.filter(line => typeof line === 'string') : [];
      return normalizeMessage({
        id: snapshot.id, recordType, recordId, authorId: note.user,
        content: { text: lines.join('\n'), lines }, created: { seconds: Number(note.timestamp) },
        edited: note.edited === true, deleted: false, source: 'legacy-note', legacy: true, isCommand: false
      });
    });
    const combined = [...messages, ...notes]
      .sort((left, right) => right.timestampMs - left.timestampMs)
      .slice(0, feedLimit);
    const decorated = combined.map(message => ({ ...message, actionState: getFeedActionState(message, actor) }));
    writeCachedFeed(cacheKey, decorated);
    onMessages(decorated);
  }

  if (!actor.uid) {
    fail(new Error('Must be authenticated to load messages'));
    return stop;
  }

  const cached = readCachedFeed(cacheKey);
  if (cached) {
    onMessages(cached.map(message => ({ ...message, actionState: getFeedActionState(message, actor) })));
  }

  // Cached snapshots can paint immediately while server snapshots reconcile the
  // same feed in the background.
  subscriptions.push(onSnapshot(
    query(messagesCollection, where('deleted', '==', false), orderBy('created', 'desc'), limit(feedLimit)),
    { includeMetadataChanges: true }, snapshot => {
      if (stopped) return;
      messageDocs = snapshot.docs;
      publish();
    }, fail
  ));
  subscriptions.push(onSnapshot(
    query(collection(db, recordType, recordId, 'notes'), orderBy('timestamp', 'desc'), limit(feedLimit)),
    { includeMetadataChanges: true }, snapshot => {
      if (stopped) return;
      noteDocs = snapshot.docs;
      publish();
    }, fail
  ));
  return stop;
}
